import React, { FC } from 'react'
import { MessageOutlined } from '@ant-design/icons'
import { Alert } from 'antd'
import { QueryCondition, ConditionOperatorType } from '@pushly/pquery-core'
import { RankingWeights, StrategyPreset, STRATEGY_PRESETS } from './vespa-strategy-presets'
import { ContentWindow } from './vespa-content-window'
import { EXCLUSION_OPERATORS, INCLUSION_OPERATORS, VespaFilterField } from '../filters/types'
import { pqueryToConditions } from '../filters/helpers'
import { PQueryDocument } from '@pushly/models/lib/interfaces/pquery-document'
import './vespa-config-summary.scss'

type VespaConfigSummaryProps = {
    weights: RankingWeights
    contentWindow: ContentWindow
    filters: PQueryDocument | null | undefined
}

/**
 * Deterministic natural-language summary of a Vespa recommendation configuration.
 *
 * Rendered in the expanded card body only — the collapsed-state header uses
 * generateSummaryText directly (as a plain ellipsis-truncated single line via
 * VespaConfigurationCard's `summary` prop). Built on antd's Alert rather than
 * a hand-rolled container: Alert already provides the icon/message/description
 * layout, color theming (type="info"), and a `styles` semantic prop for
 * per-part style overrides, which is exactly the shape this box needs — no
 * reason to hand-roll it. The fixed 3-line clamp the previous custom markup
 * had (see vespa-config-summary.scss's -webkit-line-clamp, now targeting
 * .ant-alert-description) is preserved so the card's height stays constant
 * regardless of how long the generated sentence is; text beyond the 3rd line
 * is cut with an ellipsis rather than pushing layout below it. `styles.title`
 * shrinks Alert's default title size, since "Recommendation Summary" here is
 * a small section-style label, not a prominent alert headline.
 *
 * Implements a lightweight data-to-text NLG pipeline:
 * 1. Content Selection — identifies which config dimensions are non-default/notable
 * 2. Sentence Planning — aggregates related facts and selects sentence structure
 * 3. Surface Realization — produces grammatical English with natural variation
 *
 * Phrasing is selected from variant pools using a seed derived from the config
 * itself (weights + window + filters), not from wall-clock randomness. This
 * keeps the function pure — the same configuration always renders the same
 * sentence, so there's no flicker on re-render — while different configurations
 * produce genuinely different phrasing rather than the same template with values
 * swapped in.
 */
export const VespaConfigSummary: FC<VespaConfigSummaryProps> = ({ weights, contentWindow, filters }) => {
    const summary = generateSummaryText(weights, contentWindow, filters)

    return (
        <Alert
            className="vespa-config-summary"
            type="info"
            showIcon
            icon={<MessageOutlined aria-hidden="true" />}
            title="Recommendation Summary"
            description={summary}
            styles={{
                title: { fontSize: 'var(--font-size-s)', fontWeight: 500 },
            }}
        />
    )
}

type PickFn = <T>(options: T[]) => T

// ═══════════════════════════════════════════════════════════════════════════════
// Stage 1: Content Selection
// Identify which dimensions are active/notable and extract semantic "messages"
// ═══════════════════════════════════════════════════════════════════════════════

/** A single filter condition reduced to what the summary needs: field, operator, values. */
type FilterEntry = {
    field: VespaFilterField
    operator: ConditionOperatorType
    values: string[]
}

type Message =
    | { type: 'audience-scope'; entries: FilterEntry[] }
    | { type: 'time-window'; value: number; unit: string }
    | { type: 'ranking-strategy'; strategy: ResolvedStrategy }
    | { type: 'exclusion'; entries: FilterEntry[] }

type ResolvedStrategy =
    | { preset: 'personalized' }
    | { preset: 'recent' }
    | { preset: 'trending' }
    | { preset: 'balanced' }
    | { preset: null; personalized: number; recent: number }

function selectContent(weights: RankingWeights, contentWindow: ContentWindow, conditions: QueryCondition[]): Message[] {
    const messages: Message[] = []

    // Audience scope — every inclusion operator (EQUALS, EQUALS_ANY, CONTAINS,
    // ARRAY_CONTAINS), not just the two most common ones, so no condition is
    // silently dropped from the summary.
    const inclusionEntries = conditions
        .filter((c) => INCLUSION_OPERATORS.has(c.operator))
        .map(toFilterEntry)
        .filter((e) => e.values.length > 0)
    if (inclusionEntries.length > 0) {
        messages.push({ type: 'audience-scope', entries: inclusionEntries })
    }

    // Time window — always notable since it defines eligible content
    if (contentWindow.value && contentWindow.value > 0) {
        messages.push({ type: 'time-window', value: contentWindow.value, unit: contentWindow.unit })
    }

    // Ranking strategy — always present, defines the scoring method
    messages.push({ type: 'ranking-strategy', strategy: resolveStrategy(weights) })

    // Exclusions — mirrors the inclusion set (NOT_EQUALS, NOT_EQUALS_ANY,
    // NOT_CONTAINS, ARRAY_NOT_CONTAINS).
    const exclusionEntries = conditions
        .filter((c) => EXCLUSION_OPERATORS.has(c.operator))
        .map(toFilterEntry)
        .filter((e) => e.values.length > 0)
    if (exclusionEntries.length > 0) {
        messages.push({ type: 'exclusion', entries: exclusionEntries })
    }

    return messages
}

function toFilterEntry(condition: QueryCondition): FilterEntry {
    let values: string[] = []
    if (Array.isArray(condition.value)) {
        values = condition.value.map(String)
    } else if (typeof condition.value === 'string' && condition.value.trim()) {
        values = [condition.value]
    } else if (typeof condition.value === 'number') {
        values = [String(condition.value)]
    }

    return { field: condition.property as VespaFilterField, operator: condition.operator, values }
}

function resolveStrategy(weights: RankingWeights): ResolvedStrategy {
    for (const [key, preset] of Object.entries(STRATEGY_PRESETS)) {
        if (preset.weights.personalized === weights.personalized && preset.weights.recent === weights.recent) {
            return { preset: key as StrategyPreset }
        }
    }
    return { preset: null, personalized: weights.personalized, recent: weights.recent }
}

/**
 * The primary noun-slot topic ("the single best {topic} story") is only used
 * for the simplest case: one Keywords condition matching any of a value list.
 * Anything else (a second condition, a "matches all of" condition, or a
 * Title/Description condition) can't grammatically substitute into that slot
 * without producing nonsense, so it's surfaced as a separate modifier clause
 * instead via buildScopeModifier.
 */
function resolveTopic(entries: FilterEntry[]): { topicStr: string | null; extra: FilterEntry[] } {
    if (
        entries.length === 1 &&
        entries[0].field === VespaFilterField.KEYWORDS &&
        entries[0].operator === ConditionOperatorType.EQUALS_ANY
    ) {
        return { topicStr: formatList(entries[0].values), extra: [] }
    }
    return { topicStr: null, extra: entries }
}

// ═══════════════════════════════════════════════════════════════════════════════
// Stage 2: Sentence Planning
// Decide how to aggregate messages into clauses and choose sentence structure
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Sentence structures available for realization. The planner picks one
 * based on which messages are present and what the "headline" fact is.
 */
type SentencePlan =
    | { structure: 'imperative'; verb: string; object: string; modifiers: string[] }
    | { structure: 'declarative'; subject: string; predicate: string; modifiers: string[] }
    | { structure: 'noun-led'; noun: string; postModifiers: string[] }

function plan(messages: Message[], pick: PickFn): { main: SentencePlan; coda: string | null } {
    const scope = messages.find((m) => m.type === 'audience-scope') as
        | Extract<Message, { type: 'audience-scope' }>
        | undefined
    const window = messages.find((m) => m.type === 'time-window') as
        | Extract<Message, { type: 'time-window' }>
        | undefined
    const ranking = messages.find((m) => m.type === 'ranking-strategy') as Extract<
        Message,
        { type: 'ranking-strategy' }
    >
    const exclusion = messages.find((m) => m.type === 'exclusion') as
        | Extract<Message, { type: 'exclusion' }>
        | undefined

    const { topicStr, extra } = scope ? resolveTopic(scope.entries) : { topicStr: null, extra: [] as FilterEntry[] }
    const scopeModifier = buildScopeModifier(extra, pick)
    const windowStr = window ? `the last ${window.value} ${pluralizeUnit(window.value, window.unit)}` : null
    const coda = exclusion ? buildExclusionCoda(exclusion.entries, pick) : null
    const strategy = ranking.strategy

    // ── Known presets: distinct sentence structures ──────────────────────────
    if (strategy.preset === 'personalized') {
        const verb = pick(['Send each reader', 'Deliver to each reader', 'Serve each reader'])
        const object = topicStr ? `the single best ${topicStr} story` : 'the single best story'
        const modifiers: string[] = []
        if (windowStr) modifiers.push(`from ${windowStr}`)
        if (scopeModifier) modifiers.push(scopeModifier)
        modifiers.push(
            pick([
                'personalized to what they read',
                'shaped by their reading history',
                'tailored to their past clicks',
            ]),
        )
        return { main: { structure: 'imperative', verb, object, modifiers }, coda }
    }

    if (strategy.preset === 'recent') {
        const noun = topicStr
            ? pick([
                  `The freshest ${topicStr} story`,
                  `The newest ${topicStr} piece`,
                  `Whatever's most recent on ${topicStr}`,
              ])
            : pick(['The freshest eligible story', "Whatever's most recent", 'The newest thing available'])
        const postModifiers: string[] = []
        if (windowStr) postModifiers.push(`from ${windowStr}`)
        if (scopeModifier) postModifiers.push(scopeModifier)
        postModifiers.push(pick(['sent to each reader', 'delivered to each reader', 'pushed out to each reader']))
        return { main: { structure: 'noun-led', noun, postModifiers }, coda }
    }

    if (strategy.preset === 'trending') {
        const subject = topicStr
            ? pick([
                  `${topicStr} content that's gaining traction`,
                  `${topicStr} stories on the rise`,
                  `${topicStr} pieces picking up momentum`,
              ])
            : pick(["Content that's gaining traction", "Whatever's picking up momentum", 'Stories on the rise'])
        const modifiers: string[] = []
        if (windowStr) modifiers.push(`from ${windowStr}`)
        if (scopeModifier) modifiers.push(scopeModifier)
        return {
            main: {
                structure: 'declarative',
                subject,
                // The subject clause above already conveys "trending" in
                // plain terms ("gaining traction" / "on the rise" / "picking
                // up momentum") — a predicate like "ranked by momentum" said
                // the same thing a second time in jargon-ier words, so the
                // predicate here only names the delivery action.
                predicate: pick(['sent to each reader', 'delivered to each reader', 'pushed out to each reader']),
                modifiers,
            },
            coda,
        }
    }

    if (strategy.preset === 'balanced') {
        const verb = pick(['Send each reader', 'Deliver to each reader', 'Serve each reader'])
        const object = topicStr ? `the best ${topicStr} match` : 'the best match'
        const modifiers: string[] = []
        if (windowStr) modifiers.push(`from ${windowStr}`)
        if (scopeModifier) modifiers.push(scopeModifier)
        modifiers.push(
            pick([
                'scored equally on reader interest and freshness',
                'balancing personalization and freshness evenly',
                'weighing interest and freshness the same',
            ]),
        )
        return { main: { structure: 'imperative', verb, object, modifiers }, coda }
    }

    // ── Custom weights: use actual level values for intensity-aware text ─────
    const { personalized: p, recent: r } = strategy

    const customVerb = pick(['Send each reader', 'Deliver to each reader', 'Serve each reader'])
    const rankingModifier = describeCustomWeights(p, r, pick)
    const customObject = topicStr ? `the best ${topicStr} article` : 'the best article'
    const customModifiers: string[] = []
    if (windowStr) customModifiers.push(`from ${windowStr}`)
    if (scopeModifier) customModifiers.push(scopeModifier)
    if (rankingModifier) customModifiers.push(rankingModifier)

    return {
        main: { structure: 'imperative', verb: customVerb, object: customObject, modifiers: customModifiers },
        coda,
    }
}

/**
 * Combines any inclusion entries that couldn't fit the primary noun slot
 * (a second condition, an "all of" match, or a Title/Description condition)
 * into a trailing modifier clause, e.g. "restricted to stories mentioning
 * Draft in the title".
 */
function buildScopeModifier(entries: FilterEntry[], pick: PickFn): string | null {
    if (entries.length === 0) return null
    const phrases = entries.map((e) => phraseInclusionEntry(e, pick)).filter((p): p is string => Boolean(p))
    if (phrases.length === 0) return null

    const joined = joinPhrases(phrases)
    return pick([`restricted to stories ${joined}`, `limited to content ${joined}`, `narrowed to pieces ${joined}`])
}

/**
 * Builds the "Never ..." guardrail sentence from exclusion entries. Kept
 * separate from buildScopeModifier because exclusion phrasing names what's
 * being kept out rather than describing what the recommended content does.
 */
function buildExclusionCoda(entries: FilterEntry[], pick: PickFn): string | null {
    if (entries.length === 0) return null
    const phrases = entries.map((e) => phraseExclusionEntry(e, pick)).filter((p): p is string => Boolean(p))
    if (phrases.length === 0) return null

    const joined = joinPhrases(phrases)
    return pick([
        `Never ${joined}.`,
        `Nothing ${joined} makes it through.`,
        `${capitalize(joined)} is always filtered out.`,
    ])
}

/** Phrases a single inclusion-flavored entry for use inside a modifier clause. */
function phraseInclusionEntry(entry: FilterEntry, pick: PickFn): string {
    const list = formatList(entry.values)

    if (entry.field === VespaFilterField.KEYWORDS) {
        switch (entry.operator) {
            case ConditionOperatorType.EQUALS_ANY:
                return pick([`touching on ${list}`, `about ${list}`, `mentioning ${list}`])
            case ConditionOperatorType.ARRAY_CONTAINS:
                return pick([`covering all of ${list}`, `tagged with every one of ${list}`, `matching all of ${list}`])
            default:
                return ''
        }
    }

    const fieldNoun = entry.field === VespaFilterField.TITLE ? 'title' : 'description'
    const isTitled = entry.field === VespaFilterField.TITLE

    // Text fields (Title/Description) still store a string[], since the
    // builder allows entering multiple tokens even for EQUALS/CONTAINS.
    // Branch on the actual value count rather than assuming a single value —
    // otherwise every token after the first gets silently dropped from the
    // summary.
    if (entry.values.length > 1) {
        switch (entry.operator) {
            case ConditionOperatorType.EQUALS:
                return pick([
                    `${isTitled ? 'titled' : 'described as'} exactly one of ${list}`,
                    `with a ${fieldNoun} matching ${list} exactly`,
                ])
            case ConditionOperatorType.CONTAINS:
                return pick([
                    `mentioning any of ${list} in the ${fieldNoun}`,
                    `with any of ${list} somewhere in the ${fieldNoun}`,
                ])
            default:
                return ''
        }
    }

    const value = entry.values[0] ?? ''
    switch (entry.operator) {
        case ConditionOperatorType.EQUALS:
            return pick([
                `${isTitled ? 'titled' : 'described'} exactly "${value}"`,
                `with the exact ${fieldNoun} "${value}"`,
            ])
        case ConditionOperatorType.CONTAINS:
            return pick([`mentioning "${value}" in the ${fieldNoun}`, `with "${value}" somewhere in the ${fieldNoun}`])
        default:
            return ''
    }
}

/** Phrases a single exclusion-flavored entry for use inside the "Never ..." coda. */
function phraseExclusionEntry(entry: FilterEntry, pick: PickFn): string {
    if (entry.field === VespaFilterField.KEYWORDS) {
        if (entry.operator === ConditionOperatorType.ARRAY_NOT_CONTAINS) {
            const list = formatList(entry.values)
            return pick([`anything tagged with all of ${list}`, `content covering every one of ${list}`])
        }
        // NOT_EQUALS_ANY — every one of these values is independently
        // excluded (OR semantics: matching ANY one of them is enough to
        // exclude). Previously this returned the bare "&"-joined value list
        // ("Politics & Ads"), which reads ambiguously — a reader could parse
        // "&" as "both together" (AND) rather than "either one" (the actual
        // meaning). Spelling out "or" removes that ambiguity.
        return joinPhrasesWithOr(entry.values)
    }

    const fieldNoun = entry.field === VespaFilterField.TITLE ? 'title' : 'description'
    const isTitled = entry.field === VespaFilterField.TITLE

    // Same multi-value caveat as phraseInclusionEntry — Title/Description
    // store a string[] even for EQUALS/CONTAINS, so branch on count instead
    // of always reading only the first token.
    if (entry.values.length > 1) {
        const list = formatList(entry.values)
        if (entry.operator === ConditionOperatorType.NOT_EQUALS) {
            return pick([
                `anything ${isTitled ? 'titled' : 'described as'} exactly one of ${list}`,
                `a ${fieldNoun} matching ${list} exactly`,
            ])
        }
        // NOT_CONTAINS
        return pick([`anything with any of ${list} in the ${fieldNoun}`, `a ${fieldNoun} mentioning any of ${list}`])
    }

    const value = entry.values[0] ?? ''
    if (entry.operator === ConditionOperatorType.NOT_EQUALS) {
        return pick([
            `anything ${isTitled ? 'titled' : 'described as'} exactly "${value}"`,
            `a ${fieldNoun} of exactly "${value}"`,
        ])
    }
    // NOT_CONTAINS
    return pick([`anything with "${value}" in the ${fieldNoun}`, `a ${fieldNoun} mentioning "${value}"`])
}

/**
 * Generates a natural-language modifier describing custom weight values.
 * Incorporates the actual intensity levels into the phrasing, with a couple
 * of variants per branch so the text doesn't read as a single fixed template.
 */
const LEVEL_WORDS = ['off', 'lightly', 'moderately', 'heavily', 'maximally'] as const

function describeCustomWeights(p: number, r: number, pick: PickFn): string {
    // Both off
    if (p === 0 && r === 0) {
        return pick(['scored by base relevance alone', 'ranked with no personalization or freshness boost'])
    }

    // Only personalization active
    if (r === 0) {
        return pick([
            `${LEVEL_WORDS[p]} personalized to their reading history`,
            `weighted ${LEVEL_WORDS[p]} toward their reading history`,
        ])
    }

    // Only recency active
    if (p === 0) {
        if (r === 1) return pick(['with a slight freshness preference', 'leaning slightly toward newer content'])
        if (r === 2) return pick(['favoring moderately fresh content', 'leaning fairly hard toward newer stories'])
        if (r === 3) return pick(['strongly favoring recent content', 'weighted heavily toward what just published'])
        return pick(['prioritizing the absolute freshest content', 'always favoring whatever just went live'])
    }

    // Both active — describe the mix
    if (p > r) {
        const gap = p - r
        if (gap >= 2) {
            return pick([
                `${LEVEL_WORDS[p]} personalized with a light freshness nudge`,
                'mostly personalized, with a touch of freshness mixed in',
            ])
        }
        return pick([
            'personalized to each reader with some freshness weight',
            'leaning personalized, with freshness playing a supporting role',
        ])
    }
    if (r > p) {
        const gap = r - p
        if (gap >= 2) {
            return pick([
                `${LEVEL_WORDS[r]} weighted toward freshness with light personalization`,
                'mostly about freshness, with a touch of personalization mixed in',
            ])
        }
        return pick([
            'favoring fresh content with some reader personalization',
            'leaning fresh, with personalization playing a supporting role',
        ])
    }
    // Equal non-zero
    return pick([
        `balancing personalization and freshness at ${LEVEL_WORDS[p]} intensity`,
        `holding personalization and freshness evenly at ${LEVEL_WORDS[p]} intensity`,
    ])
}

// ═══════════════════════════════════════════════════════════════════════════════
// Stage 3: Surface Realization
// Convert the sentence plan into final grammatical English
// ═══════════════════════════════════════════════════════════════════════════════

function realize(sentencePlan: SentencePlan): string {
    switch (sentencePlan.structure) {
        case 'imperative': {
            // "Send each reader the single best Chiefs story from the last 12 hours, personalized to what they read."
            const parts = [sentencePlan.verb, sentencePlan.object]
            if (sentencePlan.modifiers.length > 0) {
                parts.push(sentencePlan.modifiers[0])
                for (let i = 1; i < sentencePlan.modifiers.length; i++) {
                    parts[parts.length - 1] += `, ${sentencePlan.modifiers[i]}`
                }
            }
            return parts.join(' ') + '.'
        }
        case 'declarative': {
            // "Chiefs content that's gaining traction from the last 12 hours, sent to each reader."
            const parts = [sentencePlan.subject]
            if (sentencePlan.modifiers.length > 0) {
                parts.push(sentencePlan.modifiers.join(', '))
            }
            parts.push(sentencePlan.predicate)
            return parts.join(', ') + '.'
        }
        case 'noun-led': {
            // "The freshest Chiefs story from the last 6 hours, sent to each reader."
            const parts = [sentencePlan.noun, ...sentencePlan.postModifiers]
            return parts.join(', ') + '.'
        }
    }
}

// ═══════════════════════════════════════════════════════════════════════════════
// Pipeline Orchestration
// ═══════════════════════════════════════════════════════════════════════════════

export function generateSummaryText(
    weights: RankingWeights,
    contentWindow: ContentWindow,
    filters: PQueryDocument | null | undefined,
): string {
    const conditions = pqueryToConditions(filters)
    const pick = createPicker(buildSeed(weights, contentWindow, conditions))
    const messages = selectContent(weights, contentWindow, conditions)
    const sentencePlan = plan(messages, pick)
    const mainSentence = realize(sentencePlan.main)

    if (sentencePlan.coda) {
        return `${mainSentence} ${sentencePlan.coda}`
    }
    return mainSentence
}

// ═══════════════════════════════════════════════════════════════════════════════
// Seeded phrasing selection
//
// Wording is chosen from small variant pools rather than a single fixed
// template per branch. The pool index comes from a hash of the configuration
// itself (weights, window, and every filter condition), not from Math.random
// or the current time — so calling this twice with the same configuration
// always yields the same sentence, but two different configurations are very
// unlikely to land on the same phrasing sequence.
// ═══════════════════════════════════════════════════════════════════════════════

function buildSeed(weights: RankingWeights, contentWindow: ContentWindow, conditions: QueryCondition[]): string {
    const conditionsKey = conditions
        .map((c) => `${c.property}:${c.operator}:${JSON.stringify(c.value)}`)
        .sort()
        .join('|')
    return `${weights.personalized}-${weights.recent}-${contentWindow.value ?? ''}-${
        contentWindow.unit
    }-${conditionsKey}`
}

/* tslint:disable:no-bitwise — hashString and mulberry32 are standard 32-bit
   integer hash/PRNG constructions; the bitwise ops are the algorithms, not
   incidental style. Scoped to just these two functions. */

function hashString(str: string): number {
    let h = 1779033703 ^ str.length
    for (let i = 0; i < str.length; i++) {
        h = Math.imul(h ^ str.charCodeAt(i), 3432918353)
        h = (h << 13) | (h >>> 19)
    }
    return h >>> 0
}

/** mulberry32 — small, fast, deterministic PRNG seeded from a 32-bit integer. */
function mulberry32(seed: number): () => number {
    let a = seed
    return function next() {
        a = (a + 0x6d2b79f5) | 0
        let t = Math.imul(a ^ (a >>> 15), 1 | a)
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}

/* tslint:enable:no-bitwise */

function createPicker(seedStr: string): PickFn {
    const rng = mulberry32(hashString(seedStr))
    return function pick<T>(options: T[]): T {
        if (options.length === 0) {
            throw new Error('pick() requires at least one option')
        }
        const idx = Math.floor(rng() * options.length)
        return options[idx]
    }
}

// ═══════════════════════════════════════════════════════════════════════════════
// Utilities
// ═══════════════════════════════════════════════════════════════════════════════

function formatList(values: string[]): string {
    if (values.length === 0) return ''
    if (values.length === 1) return values[0]
    if (values.length === 2) return `${values[0]} & ${values[1]}`
    return `${values.slice(0, -1).join(', ')} & ${values[values.length - 1]}`
}

function joinPhrases(phrases: string[]): string {
    if (phrases.length === 0) return ''
    if (phrases.length === 1) return phrases[0]
    if (phrases.length === 2) return `${phrases[0]} and ${phrases[1]}`
    return `${phrases.slice(0, -1).join(', ')}, and ${phrases[phrases.length - 1]}`
}

/**
 * Same shape as joinPhrases, but with "or" instead of "and" — used
 * specifically where the underlying semantics are OR (any one of these
 * values triggers the match), so the connector word itself carries the
 * correct logical meaning instead of relying on the reader to infer it
 * from an ambiguous "&"-joined list.
 */
function joinPhrasesWithOr(phrases: string[]): string {
    if (phrases.length === 0) return ''
    if (phrases.length === 1) return phrases[0]
    if (phrases.length === 2) return `${phrases[0]} or ${phrases[1]}`
    return `${phrases.slice(0, -1).join(', ')}, or ${phrases[phrases.length - 1]}`
}

function capitalize(str: string): string {
    return str.length ? str[0].toUpperCase() + str.slice(1) : str
}

/**
 * Content Window units are stored plural ('hours' | 'days' | 'weeks'); a value
 * of 1 needs the singular form so the summary reads "the last 1 hour" rather
 * than "the last 1 hours".
 */
function pluralizeUnit(value: number, unit: string): string {
    return value === 1 ? unit.replace(/s$/, '') : unit
}
